import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SKUNK_URL } from "@/data";

const links = [
  { label: "ABOUT", to: "/about", type: "route" },
  { label: "WORK", to: "/work", type: "route" },
  { label: "SKUNK MACHINE", to: SKUNK_URL, type: "external" },
  { label: "CONTACT", to: "/contact", type: "route" },
  { label: "CLAY PRODUCTS", to: "/clay-products", type: "route" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToContact = () => {
    setOpen(false);
    navigate("/contact");
  };

  const renderLink = (l) => {
    const cls =
      "text-[11px] font-bold uppercase tracking-[0.22em] transition-colors duration-300 hover:text-[var(--orange)]";
    if (l.type === "scroll")
      return (
        <button
          key={l.label}
          data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={scrollToContact}
          className={cls}
        >
          {l.label}
        </button>
      );
    if (l.type === "external")
      return (
        <a
          key={l.label}
          data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
          href={l.to}
          target="_blank"
          rel="noopener noreferrer"
          className={cls}
        >
          {l.label}
        </a>
      );
    return (
      <Link
        key={l.label}
        data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
        to={l.to}
        onClick={() => setOpen(false)}
        className={cls}
      >
        {l.label}
      </Link>
    );
  };

  const dark = isHome && !scrolled; // white text only over the dark hero
  const solid = !isHome || scrolled;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid
          ? "bg-white/95 backdrop-blur-md border-b border-[var(--cerra-border)]"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1600px] px-5 sm:px-8 h-[68px] flex items-center justify-between">
        {/* Left: nav links (desktop) */}
        <div className="hidden lg:flex items-center gap-8 lg:order-2">
          <div className={dark ? "text-white [&_a]:text-white [&_button]:text-white" : ""}>
            <div className="flex items-center gap-8">{links.map(renderLink)}</div>
          </div>
        </div>

        {/* Logo — far left */}
        <Link to="/" data-testid="nav-logo" className="lg:order-1 flex items-center">
          <img
            src="/cerra-logo-white.png"
            alt="Cerra Design"
            className="h-[1.65rem] sm:h-[1.925rem] w-auto transition-[filter] duration-500"
            style={{ filter: dark ? "none" : "invert(1)" }}
          />
        </Link>

        {/* Right: contact button */}
        <div className="hidden lg:flex lg:order-3">
          <button
            data-testid="nav-contact-button"
            onClick={scrollToContact}
            className="bg-[var(--orange)] hover:bg-[var(--orange-hover)] text-white text-[11px] font-bold uppercase tracking-[0.22em] px-6 py-3 transition-colors duration-300"
          >
            Contact
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden order-3"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? (
            <X className={dark ? "text-white" : "text-black"} size={26} />
          ) : (
            <Menu className={dark ? "text-white" : "text-black"} size={26} />
          )}
        </button>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-[var(--cerra-border)] px-5 py-6 flex flex-col gap-5"
        >
          {links.map((l) => (
            <div key={l.label} className="text-black">
              {renderLink(l)}
            </div>
          ))}
          <button
            data-testid="mobile-contact-button"
            onClick={scrollToContact}
            className="bg-[var(--orange)] text-white text-[11px] font-bold uppercase tracking-[0.22em] px-6 py-3 mt-2"
          >
            Contact
          </button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
